import { useState } from "react";
import { useListSedecEcoles, useUpdateSedecEcole, getListSedecEcolesQueryKey } from "@/lib/backoffice-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { usePaginationState } from "@/lib/use-paginated-query";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { PaginationControls } from "@/components/PaginationControls";
import SedecShell from "@/components/layout/SedecShell";
import CsvImportCard from "@/components/CsvImportCard";
import { importEcolesCsv } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Check, X } from "lucide-react";
import { toast } from "sonner";
import { OuvrirCompteEcole } from "@/components/sedec/OuvrirCompteEcole";

export default function SedecEcoles() {
  const { page, size, setPage, setSize, resetToFirstPage } = usePaginationState(20);
  const qc = useQueryClient();
  const updateMut = useUpdateSedecEcole();
  const [q, setQ] = useState("");
  const [includeInactifs, setIncludeInactifs] = useState(false);
  const debouncedQ = useDebouncedValue(q, 300);

  const onSearchChange = (v: string) => { setQ(v); resetToFirstPage(); };
  const onToggleInactifs = () => { setIncludeInactifs((v) => !v); resetToFirstPage(); };

  const { data, isLoading } = useListSedecEcoles({
    page,
    size,
    q: debouncedQ.trim() || undefined,
    includeInactifs,
  });
  const ecoles = data?.items ?? [];
  const total = data?.total ?? 0;

  const toggle = (ecoleId: string, actif: boolean) => {
    updateMut.mutate({ ecoleId, data: { actif: !actif } }, {
      onSuccess: () => {
        toast.success(actif ? "École désactivée" : "École activée (onboardée)");
        qc.invalidateQueries({ queryKey: getListSedecEcolesQueryKey() });
      },
      onError: (e: unknown) => toast.error(friendlyError(e)),
    });
  };

  return (
    <SedecShell>
      <div className="space-y-5">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Annuaire SEDEC du diocèse</div>
            <h1 className="text-2xl font-bold text-navy">{total} {total > 1 ? "écoles" : "école"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleInactifs}
              className={`px-3 py-1.5 text-xs rounded border ${includeInactifs ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
              data-testid="toggle-inactifs"
            >
              Inclure inactives
            </button>
            <div className="relative w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => onSearchChange(e.target.value)} placeholder="Rechercher" className="pl-9" data-testid="input-search" />
            </div>
          </div>
        </header>

        <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
          {isLoading && <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Code SEDEC</th>
                    <th className="text-left">Dénomination</th>
                    <th className="text-left">Commune</th>
                    <th className="text-left">Type</th>
                    <th className="text-right">Comptes</th>
                    <th className="text-left">Onboardée le</th>
                    <th className="text-center pr-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ecoles.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">Aucune école.</td></tr>}
                  {ecoles.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-ecole-${e.id}`}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.codeEtablissementSedec ?? "·"}</td>
                      <td className="font-medium text-navy truncate max-w-[280px]">{e.denomination}</td>
                      <td className="text-slate-600">{e.communeNom ?? "·"}</td>
                      <td className="text-xs text-slate-600">{(e.typeEnseignement ?? "").replace(/_/g, " ")}</td>
                      <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.nbUsers}</td>
                      <td className="text-xs text-slate-500">{e.onboardedAt ? new Date(e.onboardedAt).toLocaleDateString("fr-FR") : "·"}</td>
                      <td className="text-center pr-4 whitespace-nowrap space-x-1">
                        <OuvrirCompteEcole ecoleId={e.id} ecoleNom={e.denomination} />
                        <button onClick={() => toggle(e.id, e.actif)} disabled={updateMut.isPending} className={`text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 ${e.actif ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`button-toggle-${e.id}`}>
                          {e.actif ? <><X className="w-3 h-3" /> Désactiver</> : <><Check className="w-3 h-3" /> Activer</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls page={page} size={size} total={total} onPageChange={setPage} onSizeChange={setSize} />
            </div>
          )}
        </Card>

        <CsvImportCard
          title="Importer des écoles"
          description="Fichier CSV avec en-tête: code,nom,typeEtab,denomination,ville,quartier,categorie,typeEnseignement"
          columns={["code", "nom", "typeEtab", "denomination", "ville", "quartier", "categorie", "typeEnseignement"]}
          exampleCsv={`code,nom,typeEtab,denomination,ville,quartier,categorie,typeEnseignement
ST-JO001,College Saint Joseph,COLLEGE,College Saint Joseph d'Abidjan,Abidjan,Cocody,DIOCESAINE,SECONDAIRE_GENERAL`}
          importFn={importEcolesCsv}
          filename="modele-ecoles.csv"
          invalidateQueries={[getListSedecEcolesQueryKey()]}
        />
      </div>
    </SedecShell>
  );
}
